import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { matchPath } from 'react-router';
import useIntl from '../../../hooks/useIntl';
import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import {
  Divider,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import useHistory from '../../../hooks/useHistory';
import useUpdateUnitMutation from './useUpdateUnitMutation';
import {
  formatDateDB,
  toMoment,
} from '@fortress-technology-solutions/fortress-component-library/utils';
import messages from './messages';
import { toastr } from 'react-redux-toastr';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useUnitQuery from '../../../hooks/data-fetching/useUnitQuery';
import UnitService from '../../../services/unitService';

const DEFAULT_VALUES = {
  affordableProgram: '',
  hudSubsidyType: '',
  hudContractNumber: '',
  unitAbatementStatus: '',
  unitAbatementStartDate: null,
  unitAbatementEndDate: null,
  hudUnitNumber: '',
  phProjectNumberSuffix: '',
  phUnitNumber: '',
  xmlUnitNumber: '',
  lihtcSetAside: '---',
  homeSetAside: '---',
  homeFixedOrFloating: '---',
  bondSetAside: '---',
};

const UNIT_ABATEMENT_STATUS_ENUM = {
  NOT_ABATED: 'NOT_ABATED',
  ABATED: 'ABATED',
};

const normalizeDisplayText = (value) =>
  typeof value === 'string' ? value.trim() : '';

const getPhProjectNumberSuffixDisplay = (phProjectData) => {
  const projectNumber = normalizeDisplayText(phProjectData?.projectNumber);
  const projectSuffix = normalizeDisplayText(phProjectData?.projectSuffix);

  if (projectNumber) {
    return projectSuffix ? `${projectNumber}-${projectSuffix}` : projectNumber;
  }

  // Backward compatibility if only legacy displayValue is provided.
  return normalizeDisplayText(phProjectData?.displayValue);
};

/** Coerce request/API errors (string rejections, Error, { message }, { networkError }, etc.) for toasts. */
const getRequestErrorMessage = (error) => {
  if (error == null) return '';
  if (typeof error === 'string') return error;
  if (typeof error.message === 'string' && error.message) return error.message;
  const networkError = error.networkError;
  if (typeof networkError === 'string') return networkError;
  if (networkError && typeof networkError.message === 'string') {
    return networkError.message;
  }
  if (typeof error.error === 'string') return error.error;
  return String(error);
};

const useUnitAffordableSetup = () => {
  const [editMode, setEditMode] = useState(false);
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId;
  const history = useHistory();
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { publicHousing } = useFlags();

  const match = matchPath(history.location.pathname, {
    path: '/property/:propertyId/unit/:unitId',
    exact: true,
    strict: false,
  });

  const { unitId, propertyId } = match?.params || {};

  const { data: unit, isLoading } = useUnitQuery({
    unitId,
    propertyId,
    organizationId,
  });

  const updateUnitMutation = useUpdateUnitMutation({
    propertyId,
    organizationId,
    unitId,
    options: {
      onSuccess: (response) => {
        if (response?.error?.length) {
          // this endpoint doesn't return an error normally
          toastr.error(response?.error);
        } else {
          toastr.success(intl.formatMessage(messages.updateUnitSuccess));
          queryClient.invalidateQueries(['unit', unitId]);
          queryClient.invalidateQueries([
            'hud-contract-options',
            unitId,
            propertyId,
            organizationId,
          ]);
          setEditMode(false);
        }
      },
      onError: (error) => {
        const message = getRequestErrorMessage(error);
        toastr.error(message || intl.formatMessage(messages.updateUnitError));
      },
    },
  });

  const programTypes = useMemo(() => {
    const fpaps = unit?.floorPlan?.floorPlanAffordablePrograms || [];
    const programs = fpaps
      .map((p) => p.propertyAffordableProgram?.masterAffordableProgram?.name)
      .filter(Boolean);
    const knownPrograms = ['HUD', 'LIHTC', 'HOME', 'BOND', 'PH'];
    const programsWithNoDetails = ['RD'];
    return {
      programs,
      isHUDFloorPlan: programs.some((p) => p.includes('HUD')),
      isLIHTCFloorPlan: programs.some((p) => p.includes('LIHTC')),
      isHOMEFloorPlan: programs.some((p) => p.includes('HOME')),
      isBONDFloorPlan: programs.some((p) => p.includes('BOND')),
      isPHFloorPlan: programs.some((p) => p === 'PH'),
      otherPrograms: programs.filter(
        (p) =>
          !knownPrograms.some((known) => p.includes(known)) &&
          !programsWithNoDetails.includes(p),
      ),
      hasAnyAffordableProgram: programs.length > 0,
    };
  }, [unit]);

  const {
    data: hudContractOptionsData,
    isLoading: isLoadingHudContractOptions,
  } = useQuery(
    ['hud-contract-options', unitId, propertyId, organizationId],
    () => {
      const unitService = new UnitService();
      return unitService.getHudContractsForUnitAffordableSetup(
        propertyId,
        organizationId,
        unitId,
      );
    },
    {
      enabled:
        !!unitId &&
        !!propertyId &&
        !!organizationId &&
        !!unit &&
        programTypes.isHUDFloorPlan,
      onError: (error) => {
        const message = getRequestErrorMessage(error);
        toastr.error(
          message || intl.formatMessage(messages.hudContractsLoadError),
        );
      },
    },
  );

  const { data: phProjectData, isLoading: isLoadingPhProject } = useQuery(
    ['ph-project-assignment', unitId, propertyId, organizationId],
    () => {
      const unitService = new UnitService();
      return unitService.getPhProjectForUnitAffordableSetup(
        propertyId,
        organizationId,
        unitId,
      );
    },
    {
      enabled:
        !!unitId &&
        !!propertyId &&
        !!organizationId &&
        !!unit &&
        programTypes.isPHFloorPlan &&
        publicHousing,
      refetchOnWindowFocus: false,
      onError: (error) => {
        const message = getRequestErrorMessage(error);
        toastr.error(
          message || intl.formatMessage(messages.phProjectLoadError),
        );
      },
    },
  );

  const hudOptions = useMemo(() => {
    const options = hudContractOptionsData?.options ?? [];
    return options.map((o) => ({
      text: o.text,
      value: o.value,
      contractNumber: o.contractNumber ?? '',
    }));
  }, [hudContractOptionsData]);

  const setAsideByProgram = useMemo(() => {
    const fprrs = unit?.floorPlan?.floorPlanRentRestrictions || [];
    const map = {};
    fprrs.forEach((fprr) => {
      const programName =
        fprr.propertyAffordableProgram?.masterAffordableProgram?.name;
      if (programName && fprr.unitRentRestriction) {
        if (!map[programName]) map[programName] = [];
        map[programName].push(fprr.unitRentRestriction);
      }
    });
    // Deduplicate and join
    Object.keys(map).forEach((key) => {
      map[key] = [...new Set(map[key])].join(', ');
    });
    return map;
  }, [unit]);

  const homeFixedOrFloating = useMemo(() => {
    return selectedProperty?.setup?.HOMEUnitType || '---';
  }, [selectedProperty]);

  const getSetAsideFor = useCallback(
    (keyword) => {
      const key = Object.keys(setAsideByProgram).find((k) =>
        k.includes(keyword),
      );
      return key ? setAsideByProgram[key] : '---';
    },
    [setAsideByProgram],
  );

  const otherProgramSetAsides = useMemo(() => {
    const result = {};
    programTypes.otherPrograms.forEach((programName) => {
      const key = `${programName.replace(/[^a-zA-Z0-9]/g, '')}SetAside`;
      result[key] = setAsideByProgram[programName] || '---';
    });
    return result;
  }, [programTypes.otherPrograms, setAsideByProgram]);

  const values = useMemo(() => {
    if (!unit) return DEFAULT_VALUES;

    const affordableProgram = (
      unit.floorPlan?.floorPlanAffordablePrograms || []
    )
      .map((p) => p.propertyAffordableProgram?.masterAffordableProgram?.name)
      .filter(Boolean)
      .join(', ');

    const selectedAffordableFundingId =
      hudContractOptionsData?.selectedAffordableFundingId ?? '';
    const unitHudSubsidyTypeId = unit?.hudSubsidyTypeId ?? '';

    const unitHudValueIsValid = unitHudSubsidyTypeId
      ? hudOptions.some((o) => o.value === unitHudSubsidyTypeId)
      : false;

    const defaultHudSubsidyType = unitHudValueIsValid
      ? unitHudSubsidyTypeId
      : selectedAffordableFundingId || '';
    const selectedOption = hudOptions.find(
      (o) => o.value === defaultHudSubsidyType,
    );

    const defaultHudContractNumber = selectedOption?.contractNumber ?? '';

    const unitAbatementStatus = unit.unitAbatementStatus || {};
    return {
      affordableProgram,
      hudSubsidyType: defaultHudSubsidyType,
      hudContractNumber: defaultHudContractNumber,
      unitAbatementStatus: unitAbatementStatus.status || '',
      unitAbatementStartDate: toMoment(unitAbatementStatus.startDate),
      unitAbatementEndDate: toMoment(unitAbatementStatus.endDate),
      hudUnitNumber: unit.hudNumber || '',
      phProjectNumberSuffix: getPhProjectNumberSuffixDisplay(phProjectData),
      phUnitNumber: unit.PHUnitNumber || '',
      xmlUnitNumber: unit.xmlUnitNumber || '',
      lihtcSetAside: getSetAsideFor('LIHTC'),
      homeSetAside: getSetAsideFor('HOME'),
      homeFixedOrFloating: homeFixedOrFloating,
      bondSetAside: getSetAsideFor('BOND'),
      ...otherProgramSetAsides,
    };
  }, [
    unit,
    hudContractOptionsData,
    hudOptions,
    phProjectData,
    getSetAsideFor,
    homeFixedOrFloating,
    otherProgramSetAsides,
  ]);

  const ReactHookFormProps = useReactHookFormProps({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
    values,
  });

  const { watch, setValue, handleSubmit, isValid } = ReactHookFormProps;
  const hudSubsidyType = watch('hudSubsidyType');
  const unitAbatementStartDate = watch('unitAbatementStartDate');
  const unitAbatementEndDate = watch('unitAbatementEndDate');

  useEffect(() => {
    const selectedOption = hudOptions.find((o) => o.value === hudSubsidyType);
    if (selectedOption) {
      setValue('hudContractNumber', selectedOption.contractNumber);
    } else {
      setValue('hudContractNumber', '');
    }
  }, [hudSubsidyType, hudOptions, setValue]);

  // The XML name field is editable for every unit that renders this card.
  const hasEditableFields = programTypes.hasAnyAffordableProgram;

  const onSave = useCallback(
    (data) => {
      const payload = {};

      if (programTypes.isHUDFloorPlan) {
        payload.unitAbatementStatus = data.unitAbatementStatus || null;
        payload.unitAbatementStartDate = data.unitAbatementStartDate
          ? formatDateDB(data.unitAbatementStartDate)
          : null;
        payload.unitAbatementEndDate = data.unitAbatementEndDate
          ? formatDateDB(data.unitAbatementEndDate)
          : null;
        payload.hudNumber = data.hudUnitNumber;
        payload.hudSubsidyTypeId = data.hudSubsidyType;
      }

      if (programTypes.isPHFloorPlan && publicHousing) {
        payload.PHUnitNumber = data.phUnitNumber;
      }

      // Not program-specific. Empty must be NULL — '' blanks the XML unit number.
      payload.xmlUnitNumber = data.xmlUnitNumber?.trim() || null;

      return updateUnitMutation.mutateAsync(payload);
    },
    [updateUnitMutation, programTypes, publicHousing],
  );

  const handleToggleEditMode = useCallback(() => {
    if (editMode) {
      handleSubmit(onSave)();
    } else {
      setEditMode(true);
    }
  }, [editMode, handleSubmit, onSave]);

  const fields = useMemo(() => {
    if (!programTypes.hasAnyAffordableProgram) return [];

    const fieldList = [
      {
        name: 'affordableProgram',
        label: intl.formatMessage(messages.affordableProgram),
        variant: 'text',
        readOnly: true,
        GridProps: {
          xs: 6,
        },
      },
      // Unit-level, so a unit with several programs still shows one XML name field.
      {
        name: 'xmlUnitNumber',
        label: intl.formatMessage(messages.xmlUnitNumber),
        variant: 'text',
        readOnly: !editMode,
        // Placeholder, not value — a placeholder is never submitted.
        placeholder: editMode ? undefined : '---',
        inputProps: { maxLength: 255 },
        GridProps: {
          xs: 6,
        },
      },
    ];

    // --- HUD Section ---
    if (programTypes.isHUDFloorPlan) {
      fieldList.push(
        {
          variant: 'custom',
          component: (
            <>
              <Typography variant={'titleMedium'} display={'block'} mb={2}>
                {intl.formatMessage(messages.hud)}
              </Typography>
              <Divider />
            </>
          ),
        },
        {
          name: 'hudSubsidyType',
          label: intl.formatMessage(messages.hudSubsidyType),
          variant: 'singleSelect',
          options: hudOptions,
          readOnly: !editMode,
          rules: {
            required: true,
          },
          InputLabelProps: {
            TooltipProps: {
              title: intl.formatMessage(messages.hudSubsidyTypeTooltip),
              children: <AlertInfoIcon />,
            },
          },
          GridProps: {
            xs: 6,
          },
        },
        {
          name: 'hudContractNumber',
          label: intl.formatMessage(messages.hudContractNumber),
          variant: 'text',
          readOnly: true,
          InputLabelProps: {
            TooltipProps: {
              title: intl.formatMessage(messages.hudContractNumberTooltip),
              children: <AlertInfoIcon />,
            },
          },
          GridProps: {
            xs: 6,
          },
        },
        {
          name: 'unitAbatementStatus',
          label: intl.formatMessage(messages.unitAbatementStatus),
          variant: 'singleSelect',
          readOnly: !editMode,
          options: [
            {
              text: intl.formatMessage(messages.notAbated),
              value: UNIT_ABATEMENT_STATUS_ENUM.NOT_ABATED,
            },
            {
              text: intl.formatMessage(messages.currentlyAbated),
              value: UNIT_ABATEMENT_STATUS_ENUM.ABATED,
            },
          ],
          GridProps: {
            xs: 6,
          },
        },
        {
          name: 'unitAbatementStartDate',
          label: intl.formatMessage(messages.unitAbatementStartDate),
          variant: 'date',
          readOnly: !editMode,
          rules: {
            required: true,
          },
          maxDate: unitAbatementEndDate,
          hidden: ({ getValues }) =>
            getValues('unitAbatementStatus') !==
            UNIT_ABATEMENT_STATUS_ENUM.ABATED,
          watchFields: ['unitAbatementStatus'],
          GridProps: {
            xs: 3,
          },
        },
        {
          name: 'unitAbatementEndDate',
          label: intl.formatMessage(messages.unitAbatementEndDate),
          variant: 'date',
          readOnly: !editMode,
          // disable until start date is filled
          disabled: ({ getValues }) =>
            getValues('unitAbatementStartDate')?._isValid !== true,
          hidden: ({ getValues }) =>
            getValues('unitAbatementStatus') !==
            UNIT_ABATEMENT_STATUS_ENUM.ABATED,
          minDate: unitAbatementStartDate,
          rules: {
            required: false,
          },
          watchFields: ['unitAbatementStatus', 'unitAbatementStartDate'],
          GridProps: {
            xs: 3,
          },
        },
        {
          name: 'hudUnitNumber',
          label: intl.formatMessage(messages.hudUnitNumber),
          variant: 'text',
          readOnly: !editMode,
          InputLabelProps: {
            TooltipProps: {
              title: intl.formatMessage(messages.hudUnitNumberTooltip),
              children: <AlertInfoIcon />,
            },
          },
          GridProps: {
            xs: 6,
          },
        },
      );
    }

    // --- Public Housing Section ---
    if (programTypes.isPHFloorPlan && publicHousing) {
      fieldList.push(
        {
          variant: 'custom',
          component: (
            <>
              <Typography variant={'titleMedium'} display={'block'} mb={2}>
                {intl.formatMessage(messages.publicHousing)}
              </Typography>
              <Divider />
            </>
          ),
        },
        {
          name: 'phProjectNumberSuffix',
          label: intl.formatMessage(messages.phProjectNumberSuffix),
          variant: 'text',
          readOnly: true,
          GridProps: {
            xs: 6,
          },
        },
        {
          name: 'phUnitNumber',
          label: intl.formatMessage(messages.phUnitNumber),
          variant: 'text',
          readOnly: !editMode,
          inputProps: { maxLength: 10 },
          GridProps: {
            xs: 6,
          },
        },
      );
    }

    // --- LIHTC Section ---
    if (programTypes.isLIHTCFloorPlan) {
      const lihtcFields = [
        {
          variant: 'custom',
          component: (
            <>
              <Typography variant={'titleMedium'} display={'block'} mb={2}>
                {intl.formatMessage(messages.lihtc)}
              </Typography>
              <Divider />
            </>
          ),
        },
      ];

      lihtcFields.push({
        name: 'lihtcSetAside',
        label: intl.formatMessage(messages.setAside),
        variant: 'text',
        readOnly: true,
        GridProps: {
          xs: 6,
        },
      });

      fieldList.push(...lihtcFields);
    }

    // --- HOME Section ---
    if (programTypes.isHOMEFloorPlan) {
      const homeFields = [
        {
          variant: 'custom',
          component: (
            <>
              <Typography variant={'titleMedium'} display={'block'} mb={2}>
                {intl.formatMessage(messages.home)}
              </Typography>
              <Divider />
            </>
          ),
        },
      ];

      homeFields.push({
        name: 'homeSetAside',
        label: intl.formatMessage(messages.setAside),
        variant: 'text',
        readOnly: true,
        GridProps: {
          xs: 6,
        },
      });

      homeFields.push({
        name: 'homeFixedOrFloating',
        label: intl.formatMessage(messages.homeFixedOrFloating),
        variant: 'text',
        readOnly: true,
        GridProps: {
          xs: 6,
        },
      });

      fieldList.push(...homeFields);
    }

    // --- BOND Section ---
    if (programTypes.isBONDFloorPlan) {
      fieldList.push(
        {
          variant: 'custom',
          component: (
            <>
              <Typography variant={'titleMedium'} display={'block'} mb={2}>
                {intl.formatMessage(messages.bond)}
              </Typography>
              <Divider />
            </>
          ),
        },
        {
          name: 'bondSetAside',
          label: intl.formatMessage(messages.setAside),
          variant: 'text',
          readOnly: true,
        },
      );
    }

    // --- Other Programs (HTF, NOAH, etc.) ---
    programTypes.otherPrograms.forEach((programName) => {
      fieldList.push(
        {
          variant: 'custom',
          component: (
            <>
              <Typography variant={'titleMedium'} display={'block'} mb={2}>
                {programName}
              </Typography>
              <Divider />
            </>
          ),
        },
        {
          name: `${programName.replace(/[^a-zA-Z0-9]/g, '')}SetAside`,
          label: intl.formatMessage(messages.setAside),
          variant: 'text',
          readOnly: true,
        },
      );
    });

    return fieldList;
  }, [
    programTypes,
    intl,
    hudOptions,
    editMode,
    unitAbatementEndDate,
    unitAbatementStartDate,
    publicHousing,
  ]);

  return {
    ReactHookFormProps,
    editMode,
    fields,
    handleToggleEditMode,
    isLoading: isLoading || isLoadingHudContractOptions || isLoadingPhProject,
    intl,
    isHUDFloorPlan: programTypes.isHUDFloorPlan,
    hasAnyAffordableProgram: programTypes.hasAnyAffordableProgram,
    hasEditableFields,
    isSubmitting: updateUnitMutation.isLoading,
    isValid,
  };
};

export default useUnitAffordableSetup;
